"use client"
import { useEffect, useRef, type HTMLAttributes } from "react"

type Run = { ready: Array<{ type: string, call: () => void }> | null }
type Vendors = {
  twttr?: { widgets?: { load?: (element: Element) => void } }
  instgrm?: { Embeds?: { process?: () => void } }
  FB?: { XFBML?: { parse?: (element: Element) => void } }
}

const RUNNABLE_TYPE = /^(|module|(text|application)\/(x-)?(java|ecma)script|text\/(javascript1\.[0-5]|jscript|livescript))$/
const NEWLINE = String.fromCharCode(10)
const EMBED_SCRIPTS = new WeakMap<Element, Run>()
const EXECUTED_INLINE = new Set<string>()
let patched = false

function scriptType(script: Element) {
  return (script.getAttribute("type") ?? "").trim().toLowerCase()
}

function callWhenReady(run: Run, target: EventTarget, type: string, listener: EventListenerOrEventListenerObject) {
  const call = () => {
    try {
      if (typeof listener === "function") listener.call(target, new Event(type))
      else listener.handleEvent(new Event(type))
    } catch (error) {
      reportError(error)
    }
  }
  if (run.ready) run.ready.push({ type, call })
  else setTimeout(call)
}

function patchDocument() {
  if (patched) return
  patched = true
  const write = document.write.bind(document)
  const writeln = document.writeln.bind(document)
  const route = (original: (...text: string[]) => void, text: string[], end: string) => {
    const current = document.currentScript
    if (current && EMBED_SCRIPTS.has(current)) {
      if (current.parentNode) current.insertAdjacentHTML("beforebegin", text.join("") + end)
    } else if (document.readyState === "loading") original(...text)
  }
  document.write = (...text: string[]) => route(write, text, "")
  document.writeln = (...text: string[]) => route(writeln, text, NEWLINE)
  for (const target of [document, window] as EventTarget[]) {
    const add = target.addEventListener.bind(target)
    target.addEventListener = (type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions) => {
      const run = document.currentScript ? EMBED_SCRIPTS.get(document.currentScript) : undefined
      if (run && listener && (type === "DOMContentLoaded" || (type === "load" && target === window))) callWhenReady(run, target, type, listener)
      else add(type, listener, options)
    }
  }
}

async function runScripts(host: HTMLElement, isCurrent: () => boolean) {
  const run: Run = { ready: [] }
  const seen = new WeakSet<Element>()
  const deferred: Element[] = []
  const next = () => Array.from(host.querySelectorAll("script")).find(script => !seen.has(script) && !EMBED_SCRIPTS.has(script))

  const execute = async (inert: Element) => {
    if (!inert.isConnected) return
    const script = document.createElement("script")
    for (const { name, value } of Array.from(inert.attributes)) script.setAttribute(name, value)
    EMBED_SCRIPTS.set(script, run)
    const onload = window.onload
    if (!inert.hasAttribute("src")) {
      const source = inert.textContent ?? ""
      script.text = scriptType(inert) !== "module" && EXECUTED_INLINE.has(source) ? "{" + source + NEWLINE + "}" : source
      EXECUTED_INLINE.add(source)
      inert.replaceWith(script)
    } else if (inert.hasAttribute("async")) {
      inert.replaceWith(script)
    } else {
      script.async = false
      await new Promise(resolve => {
        script.addEventListener("load", resolve)
        script.addEventListener("error", resolve)
        inert.replaceWith(script)
      })
    }
    if (window.onload && window.onload !== onload) {
      callWhenReady(run, window, "load", window.onload)
      window.onload = onload
    }
  }

  for (let inert = next(); inert; inert = next()) {
    if (!isCurrent()) return
    seen.add(inert)
    const type = scriptType(inert)
    if (!RUNNABLE_TYPE.test(type) || inert.hasAttribute("nomodule")) continue
    if (!inert.hasAttribute("async") && (type === "module" || (inert.hasAttribute("defer") && inert.hasAttribute("src")))) deferred.push(inert)
    else await execute(inert)
  }
  for (const inert of deferred) {
    if (!isCurrent()) return
    await execute(inert)
  }

  const ready = run.ready ?? []
  run.ready = null
  for (const { call } of ready.filter(({ type }) => type !== "load")) call()
  for (const { call } of ready.filter(({ type }) => type === "load")) call()

  const vendors = window as unknown as Vendors
  vendors.twttr?.widgets?.load?.(host)
  vendors.instgrm?.Embeds?.process?.()
  vendors.FB?.XFBML?.parse?.(host)
}

export function mountEmbed(host: HTMLElement, html: string, isCurrent: () => boolean = () => true) {
  patchDocument()
  host.innerHTML = html
  void runScripts(host, () => isCurrent() && host.isConnected)
}

export default function CodeEmbed({ code, ...rest }: HTMLAttributes<HTMLDivElement> & { code?: string | null }) {
  const ref = useRef<HTMLDivElement>(null)
  const rendered = useRef<string | null>(null)

  useEffect(() => {
    const host = ref.current
    const html = code ?? ""
    if (!host || rendered.current === html) return
    rendered.current = html
    mountEmbed(host, html, () => rendered.current === html)
  }, [code])

  return <div ref={ref} {...rest} />
}
